import { GoogleOAuthProvider } from "@react-oauth/google";
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, BrowserRouter as Router, Routes, useNavigate } from 'react-router-dom';
import ProtectedRoute from './components/protectedRoute';
import Loader from './loader';
import AdminPage from './pages/admin/admin';
import AssignTask from './pages/admin/assignTask';
import ManageTechnicians from './pages/admin/manageTechnicians';
import ManageUsers from './pages/admin/manageUsers';
import PdiReports from './pages/admin/pdiReports';
import LoginPage from './pages/auth/login';
import LoginTechnicianPage from './pages/auth/loginTechnician';
import ResetPasswordPage from './pages/auth/reset';
import SignupPage from './pages/auth/signup';
import SignupTechnicianPage from './pages/auth/signupTechnician';
import Contacts from './pages/contact';
import Home from './pages/home';
import Progress from './pages/progress';
import Service from './pages/service';
import PreTrip from './pages/technician/preTrip';
import TechnicianDashboardPage from './pages/technician/technicianDashboard';
import PDI from './pages/technician/pdi';
import PDIAdmin from './pages/admin/pdiAdmin';
import TechnicianTasksPage from './pages/technician/technicianTasks';
import UsedCar from './pages/technician/usedCar';
import TrackProgress from './pages/trackProgress';
import { loadUserFromLocalStorage } from './redux/actions';
import LandingPageEditor from "./pages/admin/landingPage";
import DropdownConfig from "./pages/admin/dropdown";
import PreTripAdmin from "./pages/admin/preTripAdmin";
import UsedCarAdmin from "./pages/admin/usedCarAdmin";

const App = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true); // Loading state
  const currentUser = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = async () => {
      try {
        await dispatch(loadUserFromLocalStorage());
      } finally {
        const timer = setTimeout(() => {
          setLoading(false);        // Stop loader after 15 seconds
        }, 3000);
      }
    };
    loadUser();
  }, [dispatch]);

  useEffect(() => {
    if (!loading) {
      // Only redirect when on the root page or login page
      if (window.location.pathname === '/' || window.location.pathname === '/login') {
        if (currentUser?.role === 'admin') {
          navigate('/admin');
        } else if (currentUser?.role === 'technician') {
          navigate('/technician-dashboard');
        } else if (isAuthenticated) {
          navigate('/');
        }
      }
    }
  }, [currentUser, isAuthenticated, loading, navigate]);  

  if (loading) {
    return <Loader />; // Show loader while loading is true
  }

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const GoogleLoginAuthWrapper = () => {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <LoginPage />
      </GoogleOAuthProvider>
    )
  }

  const GoogleSignUpAuthWrapper = () => {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <SignupPage />
      </GoogleOAuthProvider>
    )
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Home />}/>
        <Route path="/service" element={<Service />}/>
        <Route path="/contact" element={<Contacts />}/>
        <Route path="/progress" element={<Progress />}/>
        <Route path="/trackprogress/:serviceId" element={<TrackProgress />}/>
        <Route path="/login" element={<GoogleLoginAuthWrapper />} />
        <Route path="/signup" element={<GoogleSignUpAuthWrapper />} />
        <Route path="/reset-password" element={<ResetPasswordPage />}/>
        <Route path="/signup/technician" element={<SignupTechnicianPage />} />
        <Route path="/login/technician" element={<LoginTechnicianPage />} />

        {/* Protected Route for Admin Page */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/assign-tasks"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AssignTask />
            </ProtectedRoute>
          }
        />        

        <Route
          path="/manage-users"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ManageUsers />
            </ProtectedRoute>
          }
        />       

        <Route
          path="/manage-technicians"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ManageTechnicians />
            </ProtectedRoute>
          }
        />        

        <Route
          path="/pdi-reports"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PdiReports />
            </ProtectedRoute>
          }
        />                                                                  

        <Route
          path="/assign-tasks"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AssignTask />
            </ProtectedRoute>
          }
        />

        <Route
          path="/landing-page"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <LandingPageEditor />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dropdown"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DropdownConfig />
            </ProtectedRoute>
          }
        />    
        
        <Route
          path="/admin/report-pre-delivery/:reportId"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PDIAdmin />
            </ProtectedRoute>
          }
        />      

        <Route
          path="/admin/report-pre-trip/:reportId"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PreTripAdmin />
            </ProtectedRoute>
          }
        />          

        <Route
          path="/admin/report-used-car/:reportId"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UsedCarAdmin />
            </ProtectedRoute>
          }
        />              

        {/* Protected Route for Technician Dashboard */}
        <Route
            path="/technician-dashboard"
            element={
              <ProtectedRoute allowedRoles={['technician']}>
                <TechnicianDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/technician/tasks"
            element={
              <ProtectedRoute allowedRoles={['technician']}>
                <TechnicianTasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/technician/report-pre-delivery/:serviceId"
            element={
              <ProtectedRoute allowedRoles={['technician']}>
                <PDI />
              </ProtectedRoute>
            }
          />
          <Route
            path="/technician/report-pre-trip/:serviceId"
            element={
              <ProtectedRoute allowedRoles={['technician']}>
                <PreTrip />
              </ProtectedRoute>
            }
          />          
          <Route
            path="/technician/report-used-car/:serviceId"
            element={
              <ProtectedRoute allowedRoles={['technician']}>
                <UsedCar />
              </ProtectedRoute>
            }
          />
      </Routes>
    </>
  );
};

const WrappedApp = () => (
  <Router>
    <App />
  </Router>
);

export default WrappedApp;
